import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface CustomTooltipProps {
  content: string;
  children: React.ReactNode;
}

export const CustomTooltip: React.FC<CustomTooltipProps> = ({ content, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible || !triggerRef.current) return;

    const updatePosition = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      if (!triggerRect) return;

      const tooltipWidth = 320;
      const tooltipHeight = tooltipRef.current?.offsetHeight || 200;
      const margin = 8;

      // Calculate position (try to show below, if not enough space show above)
      let top = triggerRect.bottom + margin + window.scrollY;
      let left = triggerRect.left + (triggerRect.width / 2) - (tooltipWidth / 2) + window.scrollX;

      // Check if tooltip goes off screen horizontally
      if (left < margin) {
        left = margin;
      } else if (left + tooltipWidth > window.innerWidth - margin) {
        left = window.innerWidth - tooltipWidth - margin;
      }

      // Check if tooltip goes off screen vertically (show above instead)
      if (top + tooltipHeight > window.innerHeight + window.scrollY - margin) {
        top = triggerRect.top - tooltipHeight - margin + window.scrollY;
      }

      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isVisible]);

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="inline-block"
      >
        {children}
      </div>
      {isVisible && content && createPortal(
        <div
          ref={tooltipRef}
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: '320px',
          }}
        >
          <div className="bg-theme-primary border border-theme-tertiary rounded-md shadow-2xl p-3 text-xs">
            <div
              className="text-gray-200"
              dangerouslySetInnerHTML={{ __html: content }}
              style={{
                lineHeight: '1.5',
              }}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

